import { zodResolver } from '@hookform/resolvers/zod';
import { CreateWeightLogRequestSchema, type CreateWeightLogRequest } from '@onflow/shared';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import '../styles/confirm-modal.css';
import '../styles/weight-prompt-modal.css';

export interface WeightPromptModalProps {
  open: boolean;
  /**
   * Called when the user submits a valid weight. The parent owns the
   * mutation. Rejecting the promise keeps the modal open in input state
   * and surfaces the error message inline.
   */
  onLog: (weightKg: number) => Promise<void>;
  /**
   * Called when the user dismisses the prompt — via the Dismiss button,
   * ESC key, or backdrop click. Same action regardless of which state
   * (CTA or input) the modal was in.
   */
  onDismiss: () => void;
}

/**
 * Two-state daily weight-logging prompt:
 *
 *   CTA state ─ "Log weight" + "Dismiss" buttons.
 *   Input state ─ single weight input + Save + Cancel.
 *
 * The user lands in CTA state on open. Tapping "Log weight" swaps to
 * input state without unmounting (single dialog, single focus trap).
 * Tapping Cancel returns to CTA state. ESC / backdrop click dismisses
 * the prompt entirely from either state.
 *
 * Submit failures (onLog promise rejects) keep the modal in input state
 * with the error rendered inline above the buttons. Input value is
 * preserved so the user can retry.
 */
export function WeightPromptModal({ open, onLog, onDismiss }: WeightPromptModalProps) {
  const titleId = useId();
  const [view, setView] = useState<'cta' | 'input'>('cta');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const ctaButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateWeightLogRequest>({
    resolver: zodResolver(CreateWeightLogRequestSchema),
  });

  useEffect(() => {
    if (!open) {
      setView('cta');
      setSubmitError(null);
      reset({ weightKg: undefined as unknown as number });
    }
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onDismiss();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  useEffect(() => {
    if (!open) return;
    if (view === 'cta') ctaButtonRef.current?.focus();
    else inputRef.current?.focus();
  }, [open, view]);

  async function onSubmit(values: CreateWeightLogRequest): Promise<void> {
    setSubmitError(null);
    try {
      await onLog(values.weightKg);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save. Try again.');
    }
  }

  if (!open) return null;

  // register() returns its own ref; merge with the local inputRef so we can
  // programmatically focus the field when transitioning into input state.
  const weightRegister = register('weightKg', {
    setValueAs: (v) =>
      v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? undefined : Number(v),
  });

  return (
    <div className="confirm-modal__backdrop confirm-modal__backdrop--glass">
      <button
        type="button"
        className="confirm-modal__backdrop-close"
        aria-label="Close dialog"
        onClick={onDismiss}
      />
      <div
        className="confirm-modal confirm-modal--glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="confirm-modal__title">
          Body weight check-in
        </h2>
        <p className="confirm-modal__summary">
          Log today&apos;s weight to keep your plan tracking on course.
        </p>

        {view === 'cta' ? (
          <div className="confirm-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onDismiss}>
              Dismiss
            </button>
            <button
              ref={ctaButtonRef}
              type="button"
              className="btn btn-primary"
              onClick={() => setView('input')}
            >
              Log weight
            </button>
          </div>
        ) : (
          <form className="weight-prompt-modal__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="weight-prompt-modal__field">
              <input
                {...weightRegister}
                ref={(el) => {
                  weightRegister.ref(el);
                  inputRef.current = el;
                }}
                type="number"
                inputMode="decimal"
                step="0.1"
                min="20"
                max="300"
                placeholder="kg"
                aria-label="Body weight in kilograms"
                aria-invalid={!!errors.weightKg}
              />
            </div>
            {errors.weightKg ? (
              <p className="weight-prompt-modal__error" role="alert">
                {errors.weightKg.message ?? 'Enter a value between 20 and 300 kg'}
              </p>
            ) : submitError ? (
              <p className="weight-prompt-modal__error" role="alert">
                {submitError}
              </p>
            ) : null}
            <div className="confirm-modal__actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setSubmitError(null);
                  setView('cta');
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
